import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { deleteVocabByText, saveVocab } from '../../lib/englishApi.js';
import type { WordLookupEntry, WordResolver } from './types.js';

/** Rango de selección sobre los tokens de palabra (índices inclusivos). */
interface Selection {
  start: number;
  end: number;
  anchorIdx: number;
  cx: number;
  top: number;
  /** El popover se ancla ARRIBA de la palabra (no hay lugar abajo). */
  flip: boolean;
  /** La flecha, corrida desde el centro del popover hasta la palabra (px). */
  arrowDx: number;
}

export interface PhraseInfo {
  status: 'loading' | 'ready' | 'error';
  result?: WordLookupEntry;
  error?: string;
}

export interface WordSelection {
  /** `arrowDx` va a `WordPopover`: el popover se corre para no salirse; la flecha sigue a la palabra. */
  sel: { start: number; end: number; cx: number; top: number; flip: boolean; arrowDx: number } | null;
  isPhrase: boolean;
  phraseText: string;
  phraseWordCount: number;
  /** Palabra individual (sin puntuación final) para el título del popover. */
  singleWord: string;
  /** Resultado del lookup de palabra (cache-hit o fetch). */
  wordEntry: WordLookupEntry | null;
  wordLoading: boolean;
  phraseInfo: PhraseInfo | null;
  saved: boolean;
  handleWordTap: (text: string, el: HTMLElement, idx: number) => void;
  closeWord: () => void;
  confirmPhrase: () => void;
  /**
   * Guarda o quita la selección. `translation` pisa la del lookup: la tarjeta
   * del «significado de esa frase» guarda la del significado que está
   * mostrando (el alumno pudo cambiarlo tocando otro).
   */
  toggleSave: (translation?: string) => void;
}

export interface WordSelectionOptions {
  /** Resolver inyectable (conversación = `/lookup`; player = glosario local). */
  resolver: WordResolver;
  /** Habilita la selección multi-palabra (frase). Default `true` (conversación). */
  enablePhrase?: boolean;
  /** Sesión activa (atribución de costo al guardar). */
  sessionId?: string;
  /**
   * Altura estimada del popover (px) para decidir si entra abajo o hay que
   * voltearlo arriba. Default 220 (popover rico). No necesita ser exacta: sólo
   * elige el lado; el anclaje vertical real es por CSS (translateY).
   */
  estPopoverHeight?: number;
  /**
   * El popover puede salirse del contenedor a los costados, mientras quede
   * dentro del área que scrollea. Para las burbujas de la charla: una corta
   * («Yeah, of course.») es más angosta que el popover, y encerrado en ella
   * quedaba apretado contra el borde. Default `false` (se queda adentro).
   */
  overflowCard?: boolean;
  /**
   * Se ejecuta al CERRAR la selección, por cualquiera de las tres vías (botón
   * ×, click/tap afuera, Escape). Vive acá y no en el `onClose` del popover
   * porque ese sólo cubre la primera. El player lo usa para retomar la lectura
   * que pausó al abrir el cartel.
   */
  onClose?: () => void;
}

const POPOVER_HALF = 120;
const POPOVER_PAD = 8;
/** Lo más que se corre la flecha desde el centro: que no pise las esquinas redondeadas. */
const ARROW_MAX_DX = POPOVER_HALF - 22;
const DEFAULT_EST_POPOVER_H = 220;

function normWord(raw: string): string {
  return raw.toLowerCase().replace(/[^a-zà-ÿ']/gi, '');
}

/**
 * Selección de palabra/frase dentro de un contenedor (burbuja del coach o línea
 * del karaoke). El comportamiento de resolución y la habilitación de frase se
 * inyectan vía `opts`, así un mismo hook sirve a la conversación y al player sin
 * duplicar la lógica de selección/posicionamiento/guardado.
 *
 * Flujo (conversación): tap = palabra resuelta al instante (cache → o fetch con
 * mini-loading) + hint para extender; tap en otra palabra = rango (banda
 * continua) SIN disparar lookup; el lookup de frase recién se dispara al
 * confirmar. Con `enablePhrase:false` (player) cada tap selecciona UNA palabra.
 */
export function useWordSelection(
  cardRef: RefObject<HTMLElement | null>,
  wordList: ReadonlyArray<string>,
  contextLine: string,
  opts: WordSelectionOptions,
): WordSelection {
  const { resolver, sessionId } = opts;
  const enablePhrase = opts.enablePhrase ?? true;
  const estPopoverHeight = opts.estPopoverHeight ?? DEFAULT_EST_POPOVER_H;
  const overflowCard = opts.overflowCard ?? false;

  const [sel, setSel] = useState<Selection | null>(null);
  const [wordEntry, setWordEntry] = useState<WordLookupEntry | null>(null);
  const [wordLoading, setWordLoading] = useState(false);
  const [phraseInfo, setPhraseInfo] = useState<PhraseInfo | null>(null);
  const [savedKeys, setSavedKeys] = useState<Record<string, boolean>>({});
  const reqRef = useRef(0);

  const isPhrase = !!sel && sel.end > sel.start;
  const phraseWords = sel ? wordList.slice(sel.start, sel.end + 1) : [];
  const phraseText = phraseWords.join(' ');
  const singleRaw = sel && !isPhrase ? (wordList[sel.start] ?? '') : '';
  const singleWord = singleRaw.replace(/[.,!?;:]+$/, '');

  const selKey = !sel
    ? ''
    : isPhrase
      ? `p:${phraseText.toLowerCase().trim()}`
      : `w:${normWord(singleRaw)}`;
  const saved = !!savedKeys[selKey];

  const handleWordTap = useCallback(
    (_text: string, el: HTMLElement, idx: number): void => {
      const card = cardRef.current;
      if (!card || !el) return;
      const wordRect = el.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();

      // El contenedor scrolleable más cercano: decide si el popover se voltea
      // (abajo) y, con `overflowCard`, hasta dónde puede salirse a los costados.
      let scroller: HTMLElement | null = card.parentElement;
      while (scroller) {
        const oy = getComputedStyle(scroller).overflowY;
        if (oy === 'auto' || oy === 'scroll') break;
        scroller = scroller.parentElement;
      }
      const bound = scroller
        ? scroller.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight, left: 0, right: window.innerWidth };

      // Límites horizontales del popover, en coordenadas del contenedor.
      const room = overflowCard
        ? { left: bound.left - cardRect.left, right: bound.right - cardRect.left }
        : { left: 0, right: cardRect.width };
      const rawCx = wordRect.left - cardRect.left + wordRect.width / 2;
      const minCx = room.left + POPOVER_HALF + POPOVER_PAD;
      const maxCx = room.right - POPOVER_HALF - POPOVER_PAD;
      const cx =
        maxCx < minCx ? (room.left + room.right) / 2 : Math.max(minCx, Math.min(maxCx, rawCx));
      // El popover se corre para no salirse del borde; la flecha no: sigue a la palabra.
      const arrowDx = Math.max(-ARROW_MAX_DX, Math.min(ARROW_MAX_DX, rawCx - cx));
      const lineH = wordRect.height || 24;

      // ¿Entra el popover ABAJO de la palabra, o hay que voltearlo ARRIBA? Se
      // mide contra el contenedor scrolleable (en el player la transcripción
      // scrollea y el dock inferior tapaba el popover). Si abajo no hay lugar y
      // arriba sí, se voltea. El anclaje vertical real lo hace el CSS
      // (translateY), así que el `top` se da relativo al borde correcto.
      const spaceBelow = bound.bottom - wordRect.bottom;
      const spaceAbove = wordRect.top - bound.top;
      const flip = spaceBelow < estPopoverHeight + lineH + 12 && spaceAbove > spaceBelow;

      // Abajo: una línea completa debajo (deja libre la fila de abajo para
      // extender la frase). Arriba: justo encima de la palabra (el CSS sube el
      // popover por su propia altura con translateY(-100%)).
      const top = flip
        ? wordRect.top - cardRect.top - 6
        : wordRect.bottom - cardRect.top + lineH + 6;
      setSel((prev) => {
        // Sin frase, o sin selección previa → una sola palabra.
        if (!enablePhrase || !prev)
          return { start: idx, end: idx, anchorIdx: idx, cx, top, flip, arrowDx };
        const a = prev.anchorIdx;
        return { start: Math.min(a, idx), end: Math.max(a, idx), anchorIdx: a, cx, top, flip, arrowDx };
      });
    },
    [cardRef, enablePhrase, estPopoverHeight, overflowCard],
  );

  // El callback de cierre va por ref: así `closeWord` sigue siendo estable
  // (es dep del efecto de click-afuera) aunque el caller pase una arrow inline.
  const onCloseRef = useRef(opts.onClose);
  useEffect(() => {
    onCloseRef.current = opts.onClose;
  });

  const closeWord = useCallback((): void => {
    setSel(null);
    setPhraseInfo(null);
    setWordEntry(null);
    setWordLoading(false);
    onCloseRef.current?.();
  }, []);

  // Selección cambió → resolver palabra individual (cache instantáneo o fetch),
  // o limpiar el resultado de frase (la frase no se busca hasta confirmar).
  const selStart = sel?.start ?? -1;
  const selEnd = sel?.end ?? -1;
  useEffect(() => {
    setPhraseInfo(null);
    if (!sel) return;
    if (sel.end > sel.start) {
      // Es frase: no resolvemos nada hasta el confirm.
      setWordEntry(null);
      setWordLoading(false);
      return;
    }
    const word = wordList[sel.start] ?? '';
    if (!word) return;
    const cached = resolver.peekWord(word, contextLine, sel.start);
    if (cached) {
      setWordEntry(cached);
      setWordLoading(false);
      return;
    }
    // Sin cache exacta, pero puede haber una respuesta PROVISORIA local (léxico
    // del capítulo para una polisémica). Si la hay, el popover abre con
    // contenido en vez de spinner y el fetch de abajo la reemplaza al llegar.
    const provisional = resolver.peekProvisional?.(word, contextLine);
    const reqId = ++reqRef.current;
    setWordEntry(provisional ?? null);
    setWordLoading(!provisional);
    void resolver
      .resolveWord(word, contextLine, sel.start)
      .then((r) => {
        if (reqId === reqRef.current) setWordEntry(r);
      })
      .catch(() => {
        // Si abrimos con la provisoria, la dejamos: una traducción global es
        // mejor que "No pudimos traducir esta palabra".
        if (reqId === reqRef.current && !provisional) setWordEntry(null);
      })
      .finally(() => {
        if (reqId === reqRef.current) setWordLoading(false);
      });
  }, [selStart, selEnd, contextLine, sel, wordList, resolver]);

  // Cerrar al click afuera del contenedor, o con Escape (desktop).
  useEffect(() => {
    if (!sel) return;
    const onDoc = (e: Event): void => {
      const card = cardRef.current;
      if (card && !card.contains(e.target as Node)) closeWord();
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') closeWord();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [sel, cardRef, closeWord]);

  const confirmPhrase = useCallback((): void => {
    if (!sel || sel.end <= sel.start) return;
    const phrase = wordList.slice(sel.start, sel.end + 1).join(' ');
    const cached = resolver.peekWord(phrase, contextLine);
    if (cached) {
      setPhraseInfo({ status: 'ready', result: cached });
      return;
    }
    const reqId = ++reqRef.current;
    setPhraseInfo({ status: 'loading' });
    void resolver
      .resolveWord(phrase, contextLine)
      .then((r) => {
        if (reqId === reqRef.current) setPhraseInfo({ status: 'ready', result: r });
      })
      .catch(() => {
        if (reqId === reqRef.current) setPhraseInfo({ status: 'error', error: 'No pudimos traducir la frase.' });
      });
  }, [sel, wordList, contextLine, resolver]);

  const toggleSave = useCallback((shownTranslation?: string): void => {
    if (!sel) return;
    const word = isPhrase ? phraseText : singleWord;
    const translation = isPhrase
      ? phraseInfo?.result?.translation
      : (shownTranslation ?? wordEntry?.translation);
    const next = !savedKeys[selKey];
    setSavedKeys((prev) => ({ ...prev, [selKey]: next }));
    if (next) {
      void saveVocab({
        word,
        ...(translation ? { translation } : {}),
        ...(sessionId ? { sessionId } : {}),
      }).catch(() => {
        // best-effort: revertir el flag visual si falló.
        setSavedKeys((prev) => ({ ...prev, [selKey]: false }));
      });
    } else {
      // Toggle OFF real (20-H Fase 0.2): antes sólo apagaba el flag visual y
      // el servidor conservaba la entrada. El delete es idempotente
      // (`deleted:false` si ya no existía) — sólo revertimos si la request falló.
      void deleteVocabByText({ text: word }).catch(() => {
        setSavedKeys((prev) => ({ ...prev, [selKey]: true }));
      });
    }
  }, [sel, isPhrase, phraseText, singleWord, phraseInfo, wordEntry, savedKeys, selKey, sessionId]);

  return {
    sel: sel
      ? { start: sel.start, end: sel.end, cx: sel.cx, top: sel.top, flip: sel.flip, arrowDx: sel.arrowDx }
      : null,
    isPhrase,
    phraseText,
    phraseWordCount: phraseWords.length,
    singleWord,
    wordEntry,
    wordLoading,
    phraseInfo,
    saved,
    handleWordTap,
    closeWord,
    confirmPhrase,
    toggleSave,
  };
}
